import { createContext, useContext, useEffect, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useAppDispatch, useAppSelector } from '../../app/hook';
import { useGetAssistantDetailMutation } from '../../app/services/chatbot-rtk.services';
import LoadingComponent from '../../components/common/loading/loading.component';
import BotEditConfiguration from '../../components/dashboard/chatbot-edit/bot-edit-configuration';
import BotEditHistoryMessage from '../../components/dashboard/chatbot-edit/bot-edit-history-message';
import ChatbotCharts from '../../components/dashboard/chatbot-edit/components/chatbot-charts';
import { MultiLanguageUrl } from '../../constants/multiLanguage.constant';
import { handleInitializeEditAppearance, selectAppearanceState } from '../../features/appearance/appearanceSlice';
import BotAppearanceEdit from '../../features/appearance/BotAppearanceEdit';
import { handleInitializeEditWizard, handleResetWizard, wizardState } from '../../features/bot/botSlice';
import KnowledgeSeederEdit from '../../features/bot/KnowledgeSeederEdit';
import { AIKittyType, BotCode, McBotFaceType } from '../../features/bot/types';
import VisibilityManagerEdit from '../../features/bot/VisibilityManagerEdit';
import { BotCategoryEnum } from '../../utils/BotCategoryEnum';
import { BotTypeEnum } from '../../utils/BotTypeEnum';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';

export const ChatbotEditContext = createContext<any>(null);

const ChatbotEditProvider = ({ children }: { children: any }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const wizard = useAppSelector(wizardState);
  const tab = queryParams.get('tab');
  const botId = queryParams.get('botId');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [getAssistantDetails, getAssistantDetailsStatus] = useGetAssistantDetailMutation();

  let tabs = [
    {
      title: 'Wizard/EditOptions/EditBotProfile',
      name: 'config',
      component: <BotEditConfiguration />,
    },
    {
      title: 'Wizard/EditOptions/Appearance',
      name: 'appearance',
      selected: false,
      component: <BotAppearanceEdit />,
    },
    {
      title: 'Wizard/EditOptions/OperatingHours',
      selected: false,
      name: 'operating-hours',
      component: <VisibilityManagerEdit />,
    },
  ];

  const [value, setValue] = useState<{
    tab: string | null;
    botId: string | null;
    title: string | null;
    templateId: number | undefined;
    assistantType: string | undefined;
    isLoading: boolean;
    tabs: any[] | null;
  } | null>({
    tab: null,
    botId: null,
    title: null,
    templateId: undefined,
    assistantType: undefined,
    isLoading: true,
    tabs: [],
  });

  useEffect(() => {
    setIsLoading(true);
    let templateId: any = undefined;
    let assistantType: any = undefined;
    if (botId) {
      dispatch(handleResetWizard());
      getAssistantDetails(botId)
        .unwrap()
        .then(value => {
          dispatch(
            handleInitializeEditWizard({
              title: value.title,
              id: value.id,
              bot_code: value.type as BotCode,
              bot_type:
                (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO || (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                  ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                  : BotCategoryEnum.AI_BASED_CHATBOT,
              bot_config:
                (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO || (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                  ? ({
                      bot_code: value.type,
                      title: value.title,
                      template_id: 1,
                    } as McBotFaceType)
                  : ({ ...value.config } as AIKittyType),
              showModal: false,
            })
          );
          if ((value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO || (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO) {
            templateId = 1;
          } else {
            if (value?.config?.assistant) assistantType = value?.config?.assistant;
          }
          dispatch(
            handleInitializeEditAppearance({
              id: value.id,
              bot_code: value.type as BotCode,
              bot_type:
                (value.type as BotCode) === BotTypeEnum.VIDEO_SCENARIO || (value.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                  ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                  : BotCategoryEnum.AI_BASED_CHATBOT,
              title: value.title,
              configs: value.config.settings,
            })
          );

          if (value.type == BotTypeEnum.AI_WITH_KNOWLEDGE_BASE || value.type == BotTypeEnum.HUMAN_CLONE) {
            if (!tabs.find(dr => dr.name == 'knowledgeBases')) {
              tabs = [
                ...tabs,
                {
                  title: 'Wizard/EditOptions/KnowledgeBase',
                  name: 'knowledgeBases',
                  component: <KnowledgeSeederEdit />,
                },
              ];
            }
          }

          if (value.type != BotTypeEnum.HUMAN_CLONE) {
            if (!tabs.find(dr => dr.name == 'statistics')) {
              tabs = [
                ...tabs,
                {
                  title: 'Wizard/EditOptions/Statistics',
                  name: 'statistics',
                  selected: false,
                  component: <ChatbotCharts />,
                },
              ];
            }
          }

          if (
            value.type == 'KITTY_01' ||
            value.type == BotTypeEnum.AI_READY_TO_USE ||
            value.type == BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
            value.type == BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY ||
            value.type == BotTypeEnum.CALLBOT
          ) {
            if (!tabs.find(dr => dr.name == 'history-message')) {
              tabs = [
                ...tabs,
                {
                  title: 'Wizard/EditOptions/HistoryConversation',
                  selected: false,
                  name: 'history-message',
                  component: <BotEditHistoryMessage />,
                },
              ];
            }
          }

          setValue({
            tabs: tabs,
            tab: tab,
            botId: botId,
            title: value.title,
            templateId: templateId,
            assistantType: assistantType,
            isLoading: false,
          });
        })
        .catch(err => {
          toast.error(err.data.message || err.message);
          setIsLoading(false);
        });
    }
  }, [location.search]);

  return (
    <ChatbotEditContext.Provider value={{ value, setValue }}>
      <>
        {value && value.isLoading == false && wizard.bot_config && children}
        {!value || value.isLoading == true || (!wizard.bot_config && <LoadingComponent />)}
      </>
    </ChatbotEditContext.Provider>
  );
};

function ChatbotEdit() {
  return (
    <ChatbotEditProvider>
      <ChatbotEditContent />
    </ChatbotEditProvider>
  );
}

export default WrappedErrorBoundary(ChatbotEdit);

const ChatbotEditContent = () => {
  const chatBotEditValue = useContext(ChatbotEditContext);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { t, i18n } = useTranslation();
  const tab = queryParams.get('tab');
  const botId = queryParams.get('botId');
  const appearanceState = useAppSelector(selectAppearanceState);
  const navigate = useNavigate();
  const goToTab = (tabName: string) => {
    navigate(`/${MultiLanguageUrl[i18n.language]}/control-panel/chatbot-edit?botId=${botId}&tab=${tabName}`);
  };

  const [tabData, setTabData] = useState<any[]>([]);

  useEffect(() => {
    if (tabData.length > 0 && tab && appearanceState.bot_code) {
      const index = tabData?.findIndex(dr => dr.name == tab);

      setTabData(prev => {
        return prev.map((tab, i) => {
          if (i === index) {
            return { ...tab, selected: true };
          } else {
            return { ...tab, selected: false };
          }
        });
      });
    }
  }, [location.pathname, location.search, tabData.length]);

  const handleTabClick = (index: number) => {
    goToTab(tabData[index].name);
  };

  useEffect(() => {
    if (chatBotEditValue?.value) {
      setTabData(
        chatBotEditValue?.value.tabs.map((prev: any) => ({
          ...prev,
          selected: false,
        }))
      );
    }
  }, [chatBotEditValue?.value?.tabs?.length]);

  return (
    <>
      <div className="lg:tw-flex tw-flex-col tw-pt-5 tw-px-5 tw-hidden">
        <div className="tw-w-full  tw-h-[50px] tw-relative tw-overflow-x-auto  tw-overflow-y-hidden">
          <div className="lg:tw-w-full tw-h-px tw-left-0 tw-top-[35px] tw-absolute tw-bg-gray-300 tw-hidden lg:tw-block" />
          <div className="tw-left-0 tw-top-0 tw-absolute tw-justify-center tw-items-center tw-gap-8 tw-inline-flex">
            {tabData.map((tab, i) => (
              <div
                key={i}
                role="button"
                tabIndex={0}
                className="tw-flex-col tw-w-full tw-justify-center tw-items-center tw-inline-flex tw-cursor-pointer"
                onClick={() => {
                  handleTabClick(i);
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleTabClick(i);
                  }
                }}
              >
                <div className="tw-px-1 tw-pb-4 tw-justify-center tw-items-center tw-inline-flex">
                  <div
                    className={`${
                      tab.selected ? 'tw-text-primary' : ''
                    } tw-text-sm tw-font-medium tw-font-['Lexend'] tw-leading-tight tw-w-[200px] tw-text-center`}
                  >
                    {t(tab.title)}
                  </div>
                </div>
                {tab.selected && <div className="tw-self-stretch tw-h-0.5 tw-bg-blue-500" />}
              </div>
            ))}
          </div>
        </div>
        <div className="tw-w-full">{tabData.find(dr => dr.selected == true)?.component}</div>
      </div>

      <div className="tw-flex lg:tw-hidden tw-w-full">
        {tabData?.findIndex(dr => dr.name == tab)?.toString() && tabData?.findIndex(dr => dr.name == tab)?.toString() != '-1' && (
          <Accordion defaultActiveKey={tabData?.findIndex(dr => dr.name == tab)?.toString()} className="tw-w-full">
            {tabData.map((tab, i) => (
              <Accordion.Item
                id={tab.name}
                key={i}
                eventKey={i.toString()}
                onClick={() => {
                  handleTabClick(i);
                }}
              >
                <Accordion.Header>{t(tab.title)}</Accordion.Header>
                <Accordion.Body className="tw-bg-gray-50">{tab.component}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        )}
      </div>
    </>
  );
};
